/**
 * Shopify Custom Wishlist Plugin v3
 */
(function () {
  'use strict';

  const cfg        = window.WishlistConfig || {};
  const API_URL    = (cfg.apiUrl    || '').replace(/\/$/, '');
  const API_SECRET = cfg.apiSecret  || '';
  const CURRENCY   = cfg.currency   || '₹';

  if (!API_URL) {
    console.warn('[Wishlist] WishlistConfig.apiUrl is not set. Plugin disabled.');
    return;
  }

  const LS_NAME_KEY  = 'wl_name';
  const LS_PHONE_KEY = 'wl_phone';
  const LS_ITEMS_KEY = 'wl_items';
  const LS_DATA_KEY  = 'wl_data';

  // ── Local state helpers ─────────────────────────────────
  function getName()    { return localStorage.getItem(LS_NAME_KEY)  || ''; }
  function saveName(n)  { localStorage.setItem(LS_NAME_KEY, n); }
  function getPhone()   { return localStorage.getItem(LS_PHONE_KEY) || ''; }
  function savePhone(p) { localStorage.setItem(LS_PHONE_KEY, p); }

  function getLocalItems() {
    try { return JSON.parse(localStorage.getItem(LS_ITEMS_KEY) || '{}'); }
    catch { return {}; }
  }
  function setLocalItem(id, val) {
    const items = getLocalItems();
    if (val) items[String(id)] = true;
    else delete items[String(id)];
    localStorage.setItem(LS_ITEMS_KEY, JSON.stringify(items));
    updateCounter();
  }
  function isWishlisted(id) { return !!getLocalItems()[String(id)]; }

  function getLocalData() {
    try { return JSON.parse(localStorage.getItem(LS_DATA_KEY) || '{}'); }
    catch { return {}; }
  }
  function saveLocalData(productData) {
    const data = getLocalData();
    data[String(productData.product_id)] = productData;
    localStorage.setItem(LS_DATA_KEY, JSON.stringify(data));
  }
  function removeLocalData(productId) {
    const data = getLocalData();
    delete data[String(productId)];
    localStorage.setItem(LS_DATA_KEY, JSON.stringify(data));
  }

  // ── Resolved user identity ──────────────────────────────
  // Two scenarios only:
  //   1) Returning user  — wl_phone exists in localStorage → skip modal
  //   2) New user        — no phone found → open modal
  function resolvedPhone() {
    return getPhone();
  }
  function resolvedName() {
    return getName();
  }

  // ── API calls ───────────────────────────────────────────
  async function apiCall(method, body) {
    const opts = {
      method,
      headers: {
        'Content-Type': 'application/json',
        'x-wishlist-secret': API_SECRET,
      },
    };
    if (body && method !== 'GET' && method !== 'HEAD') {
      opts.body = JSON.stringify(body);
    }
    const url = method === 'GET'
      ? `${API_URL}/api/wishlist?phone=${encodeURIComponent(body.phone)}`
      : `${API_URL}/api/wishlist`;
    const res = await fetch(url, opts);
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || `HTTP ${res.status}`);
    }
    return res.json();
  }

  async function apiAdd(phone, productData)   { return apiCall('POST',   { phone, ...productData }); }
  async function apiRemove(phone, product_id) { return apiCall('DELETE', { phone, product_id }); }
  async function apiGet(phone)                { return apiCall('GET',    { phone }); }

  // ── Sync from server ────────────────────────────────────
  async function syncFromServer() {
    const phone = resolvedPhone();
    updateCounter();
    if (!phone) return;

    try {
      const data = await apiGet(phone);
      const serverItems = data.wishlist || [];

      // Same rule as the original plugin: an empty server list never wipes items saved locally.
      if (serverItems.length > 0) {
        const fresh = {};
        const dataCache = {};
        serverItems.forEach(entry => {
          fresh[String(entry.product_id)] = true;
          dataCache[String(entry.product_id)] = {
            product_id:       entry.product_id,
            product_title:    entry.product_title,
            product_handle:   entry.product_handle,
            variant_id:       entry.variant_id,
            product_image:    entry.product_image,
            product_price:    entry.product_price,
            compare_at_price: entry.compare_at_price || '',
            added_at:         entry.added_at,
          };
        });
        localStorage.setItem(LS_ITEMS_KEY, JSON.stringify(fresh));
        localStorage.setItem(LS_DATA_KEY, JSON.stringify(dataCache));
      }

      updateAllButtons();
      updateCounter();

      if (window.WishlistPage && typeof window.WishlistPage.refresh === 'function') {
        window.WishlistPage.refresh();
      }
    } catch (e) {
      console.warn('[Wishlist] Sync failed:', e.message);
      updateCounter();
    }
  }

  // ── Counter (badge in header) ───────────────────────────
  function updateCounter() {
    const items = getLocalItems();
    const count = Object.keys(items).length;

    document.querySelectorAll('[data-wishlist-count]').forEach(el => {
      el.textContent = count;
      if (count > 0) {
        el.style.cssText = 'display: flex !important; align-items: center; justify-content: center;';
        el.classList.add('is-visible');
        el.setAttribute('data-count', count);
      } else {
        el.style.cssText = 'display: none !important;';
        el.classList.remove('is-visible');
        el.removeAttribute('data-count');
      }
    });

    document.querySelectorAll('[data-wishlist-link]').forEach(el => {
      el.classList.toggle('has-items', count > 0);
    });
  }

  // ── Heart SVG state ─────────────────────────────────────
  function updateHeartSVG(btn) {
    const active = btn.classList.contains('is-wishlisted');
    btn.querySelectorAll('svg, svg g, svg path').forEach(el => {
      el.removeAttribute('style');
      if (active) {
        el.style.fill        = '#e63946';
        el.style.stroke      = '#e63946';
        el.style.strokeWidth = '2';
        el.style.opacity     = '1';
      } else {
        el.style.fill        = 'none';
        el.style.stroke      = '#333';
        el.style.strokeWidth = '4';
        el.style.opacity     = '1';
      }
    });
  }

  // ── Button state ────────────────────────────────────────
  function updateAllButtons() {
    // Read localStorage once per refresh instead of reparsing it for every
    // product card on the page.
    const items = getLocalItems();
    document.querySelectorAll('.wishlist-btn[data-product-id]').forEach(btn => {
      const id     = String(btn.dataset.productId || '');
      const active = !!items[id];
      btn.classList.toggle('is-wishlisted', active);
      btn.setAttribute('aria-label', active ? 'Remove from wishlist' : 'Add to wishlist');
      btn.setAttribute('aria-pressed', String(active));
      updateHeartSVG(btn);
    });
  }

  function setButtonLoading(btn, loading) {
    btn.disabled = loading;
    if (loading) btn.classList.add('wl-loading');
    else btn.classList.remove('wl-loading');
  }

  // ── Toast ───────────────────────────────────────────────
  let toastTimer = null;
  function showToast(msg, type = 'success') {
    let toast = document.getElementById('wl-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'wl-toast';
      toast.className = 'wl-toast';
      document.body.appendChild(toast);
    }
    toast.textContent = msg;
    toast.className = `wl-toast is-${type}`;
    void toast.offsetHeight;
    toast.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toast.classList.remove('is-visible'), 2800);
  }

  // ── Modal ───────────────────────────────────────────────
  let pendingProductData = null;
  let pendingButton      = null;

  function openModal(btn, productData) {
    pendingProductData = productData;
    pendingButton      = btn;
    const overlay = document.getElementById('wl-modal-overlay');
    if (!overlay) return;

    const nameEl  = overlay.querySelector('.wl-modal__product-name');
    const priceEl = overlay.querySelector('.wl-modal__product-price');
    const imgEl   = overlay.querySelector('.wl-modal__product-img');
    const phEl    = overlay.querySelector('.wl-modal__product-img-placeholder');

    if (nameEl)  nameEl.textContent  = productData.product_title || '';
    if (priceEl) priceEl.textContent = productData.product_price ? `${CURRENCY}${productData.product_price}` : '';

    if (productData.product_image) {
      if (imgEl) { imgEl.src = productData.product_image; imgEl.style.display = 'block'; }
      if (phEl)   phEl.style.display = 'none';
    } else {
      if (imgEl)  imgEl.style.display = 'none';
      if (phEl)   phEl.style.display = 'flex';
    }

    const nameInput = overlay.querySelector('#wl-name-input');
    if (nameInput && getName()) nameInput.value = getName();

    const contactInput = overlay.querySelector('#wl-contact-input');
    if (contactInput && getPhone()) contactInput.value = getPhone();

    showModalForm(overlay);
    clearAllErrors(overlay);
    overlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';

    if (nameInput && !nameInput.value) {
      setTimeout(() => nameInput.focus(), 250);
    } else if (contactInput) {
      setTimeout(() => contactInput.focus(), 250);
    }
  }

  function closeModal() {
    const overlay = document.getElementById('wl-modal-overlay');
    if (!overlay) return;
    overlay.classList.remove('is-open');
    document.body.style.overflow = '';
    pendingProductData = null;
    pendingButton      = null;
  }

  function showModalForm(overlay) {
    overlay.querySelector('.wl-modal__form-section').style.display = 'block';
    overlay.querySelector('.wl-modal__success').classList.remove('is-visible');
  }
  function showModalSuccess(overlay) {
    overlay.querySelector('.wl-modal__form-section').style.display = 'none';
    overlay.querySelector('.wl-modal__success').classList.add('is-visible');
    setTimeout(() => closeModal(), 1800);
  }

  // ── Error helpers ───────────────────────────────────────
  function showFieldError(overlay, inputSelector, errorSelector, msg) {
    const errEl = overlay.querySelector(errorSelector);
    const input = overlay.querySelector(inputSelector);
    if (errEl) { errEl.textContent = msg; errEl.classList.add('is-visible'); }
    if (input) input.classList.add('has-error');
  }

  function clearFieldError(overlay, inputSelector, errorSelector) {
    const errEl = overlay.querySelector(errorSelector);
    const input = overlay.querySelector(inputSelector);
    if (errEl) { errEl.textContent = ''; errEl.classList.remove('is-visible'); }
    if (input) input.classList.remove('has-error');
  }

  function clearAllErrors(overlay) {
    clearFieldError(overlay, '#wl-name-input',    '#wl-name-error');
    clearFieldError(overlay, '#wl-contact-input', '#wl-phone-error');
  }

  // ── Validation ──────────────────────────────────────────
  function validateContact(value) {
    const v = value.trim();
    const emailRegex = /^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/;
    if (emailRegex.test(v)) return null;
    const digits = v.replace(/\D/g, '');
    if (digits.length === 10) return null;
    return 'Please enter a valid 10-digit phone number or email address.';
  }

  async function submitModal() {
    const overlay      = document.getElementById('wl-modal-overlay');
    const contactInput = overlay.querySelector('#wl-contact-input');
    const nameInput    = overlay.querySelector('#wl-name-input');
    const btn          = overlay.querySelector('#wl-modal-submit');
    if (!contactInput || !overlay) return;

    const contact = contactInput.value.trim();
    const name    = nameInput ? nameInput.value.trim() : '';

    clearAllErrors(overlay);

    let hasError = false;

    if (!name) {
      showFieldError(overlay, '#wl-name-input', '#wl-name-error', 'Please enter your name.');
      hasError = true;
    }

    const contactErr = validateContact(contact);
    if (contactErr) {
      showFieldError(overlay, '#wl-contact-input', '#wl-phone-error', contactErr);
      hasError = true;
    }

    if (hasError) {
      if (!name && nameInput) nameInput.focus();
      else if (contactErr && contactInput) contactInput.focus();
      return;
    }

    btn.disabled    = true;
    btn.textContent = 'Saving...';

    try {
      const dataToSend = { ...pendingProductData, customer_name: name };
      await apiAdd(contact, dataToSend);
      savePhone(contact);
      saveName(name);
      setLocalItem(pendingProductData.product_id, true);
      saveLocalData(pendingProductData);

      updateAllButtons();
      if (pendingButton) {
        pendingButton.classList.add('wl-animate');
        setTimeout(() => pendingButton.classList.remove('wl-animate'), 400);
      }
      showModalSuccess(overlay);
    } catch (e) {
      showFieldError(overlay, '#wl-contact-input', '#wl-phone-error', 'Something went wrong. Please try again.');
      console.error('[Wishlist] Add failed:', e);
    } finally {
      btn.disabled    = false;
      btn.textContent = 'Save to Wishlist';
    }
  }

  // ── Click handler ───────────────────────────────────────
  async function handleWishlistClick(btn) {
    const productId = btn.dataset.productId;
    if (!productId) return;

    const productData = {
      product_id:       productId,
      product_title:    btn.dataset.productTitle  || '',
      product_handle:   btn.dataset.productHandle || '',
      variant_id:       btn.dataset.variantId     || '',
      product_image:    btn.dataset.productImage  || '',
      product_price:    btn.dataset.productPrice  || '',
      compare_at_price: btn.dataset.comparePrice  || '',
    };

    // ── REMOVE flow ─────────────────────────────────────
    if (isWishlisted(productId)) {
      const phone = resolvedPhone();
      setButtonLoading(btn, true);
      
      // Try to remove from API if phone exists, but don't fail if it does
      if (phone) {
        try {
          await apiRemove(phone, productId);
        } catch (e) {
          console.warn('[Wishlist] API remove failed, removing locally:', e.message);
          // Continue with local removal even if API fails
        }
      }
      
      // Always remove locally
      setLocalItem(productId, false);
      removeLocalData(productId);
      updateAllButtons();
      showToast('Removed from wishlist', 'success');
      
      if (window.WishlistPage && typeof window.WishlistPage.refresh === 'function') {
        window.WishlistPage.refresh();
      }
      
      setButtonLoading(btn, false);
      return;
    }

    // ── ADD flow ────────────────────────────────────────
    const phone = resolvedPhone();
    const name  = resolvedName();

    if (phone) {
      setButtonLoading(btn, true);
      try {
        const dataToSend = { ...productData, customer_name: name };
        await apiAdd(phone, dataToSend);
        setLocalItem(productId, true);
        saveLocalData(productData);

        updateAllButtons();
        btn.classList.add('wl-animate');
        setTimeout(() => btn.classList.remove('wl-animate'), 400);
        showToast('Added to wishlist!', 'success');
      } catch (e) {
        showToast('Could not add to wishlist. Try again.', 'error');
      } finally {
        setButtonLoading(btn, false);
      }
      return;
    }

    // No phone found → open modal
    openModal(btn, productData);
  }

  function initButtons() {
    document.addEventListener('click', function (e) {
      const btn = e.target.closest('.wishlist-btn[data-product-id]');
      if (btn) {
        e.preventDefault();
        e.stopPropagation();
        handleWishlistClick(btn);
      }
    });
  }

  function initModal() {
    const overlay = document.getElementById('wl-modal-overlay');
    if (!overlay) return;

    overlay.addEventListener('click', function (e) {
      if (e.target === overlay) closeModal();
    });

    overlay.querySelector('.wl-modal__close')?.addEventListener('click', closeModal);
    overlay.querySelector('#wl-modal-submit')?.addEventListener('click', submitModal);

    overlay.querySelector('#wl-name-input')?.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') {
        e.preventDefault();
        const contactInput = overlay.querySelector('#wl-contact-input');
        if (contactInput && !contactInput.value.trim()) {
          contactInput.focus();
        } else {
          submitModal();
        }
      }
    });

    overlay.querySelector('#wl-contact-input')?.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') { e.preventDefault(); submitModal(); }
    });

    overlay.querySelector('#wl-name-input')?.addEventListener('input', function () {
      clearFieldError(overlay, '#wl-name-input', '#wl-name-error');
    });
    overlay.querySelector('#wl-contact-input')?.addEventListener('input', function () {
      clearFieldError(overlay, '#wl-contact-input', '#wl-phone-error');
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeModal();
    });
  }

  // ── Helpers ─────────────────────────────────────────────
  function esc(s) {
    return String(s || '')
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function https(url) {
    if (!url) return '';
    return url.startsWith('//') ? 'https:' + url : url;
  }

  function formatMoney(paise) {
    const amount = paise / 100;
    return '₹' + amount.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  }

  // ── Wishlist page renderer ──────────────────────────────
  const productCache = {};
  async function fetchProduct(handle) {
    if (productCache[handle]) return productCache[handle];
    try {
      const res = await fetch('/products/' + handle + '.js');
      if (!res.ok) return null;
      const p = await res.json();
      productCache[handle] = p;
      return p;
    } catch (e) {
      return null;
    }
  }

  function buildCard(localData, shopifyProduct) {
    if (!shopifyProduct) return '';

    const productId  = localData.product_id;
    const handle     = shopifyProduct.handle;
    const title      = shopifyProduct.title;
    const url        = '/products/' + handle;
    const vendor     = shopifyProduct.vendor || '';
    const available  = shopifyProduct.available;

    const savedVariantId = String(localData.variant_id || '');
    const variants       = shopifyProduct.variants || [];
    const variant        = variants.find(v => String(v.id) === savedVariantId) || variants[0] || {};

    const price        = variant.price            || 0;
    const comparePrice = variant.compare_at_price || 0;
    const onSale       = comparePrice > price && price > 0;
    const pct          = onSale ? Math.round((comparePrice - price) / comparePrice * 100) : 0;

    const imgSrc  = https(localData.product_image || (shopifyProduct.images && shopifyProduct.images[0] && shopifyProduct.images[0].src) || '');
    const wlPrice = (price / 100).toFixed(0);
    const wlCmp   = (comparePrice / 100).toFixed(0);

    return `
    <div class="product-card-wrapper wl-product-card-wrapper" data-product-handle="${esc(handle)}" data-product-id="${esc(productId)}">
      <product-card handle="${esc(handle)}" class="product-card product-card--blends">

        <div class="product-card__figure">

          ${!available
            ? `<div class="product-card__badge-list"><div class="badge badge--sold-out">Sold Out</div></div>`
            : onSale
              ? `<div class="product-card__badge-list"><div class="badge badge--on-sale">Sale</div></div>`
              : ''}

          <button
            class="wishlist-btn wishlist-btn--card is-wishlisted"
            type="button"
            aria-label="Remove from wishlist"
            aria-pressed="true"
            data-product-id="${esc(productId)}"
            data-product-title="${esc(title)}"
            data-product-handle="${esc(handle)}"
            data-variant-id="${esc(variant.id || '')}"
            data-product-image="${esc(imgSrc)}"
            data-product-price="${esc(wlPrice)}"
            data-compare-price="${esc(wlCmp)}"
          >
            <svg aria-hidden="true" focusable="false" role="presentation" viewBox="-4 -4 98 93" width="24" height="24">
              <path d="M45 84.334L6.802 46.136C2.416 41.75 0 35.918 0 29.716c0-6.203 2.416-12.034 6.802-16.42 4.386-4.386 10.217-6.802 16.42-6.802 6.203 0 12.034 2.416 16.42 6.802L45 18.654l5.358-5.358c4.386-4.386 10.218-6.802 16.42-6.802 6.203 0 12.034 2.416 16.42 6.802C87.585 17.682 90 23.513 90 29.716c0 6.203-2.415 12.034-6.802 16.42L45 84.334z"
                fill="none" stroke="currentColor" stroke-width="4" stroke-linejoin="round"/>
            </svg>
          </button>

          <a href="${esc(url)}" class="product-card__image-wrapper">
            ${imgSrc
              ? `<img class="product-card__image product-card__image--primary aspect-adapt" src="${esc(imgSrc)}" alt="${esc(title)}" loading="lazy" style="object-fit:cover;width:100%;height:100%;">`
              : `<div class="product-card__image product-card__image--primary" style="aspect-ratio:1/1;background:#f0f0f0;"></div>`}
          </a>

          <div class="product-card__quick-buy">
            ${available
              ? `<form id="wl-form-${esc(productId)}-${esc(variant.id)}" method="post" action="${Shopify.routes.root}cart/add.js" accept-charset="UTF-8" style="display:none;">
                   <input type="hidden" name="id" value="${esc(variant.id)}">
                   <input type="hidden" name="quantity" value="1">
                 </form>
                 <button type="button" class="button button--primary product-card__atc wl-quick-add-btn" onclick="window.submitWishlistCart(event, 'wl-form-${esc(productId)}-${esc(variant.id)}')" style="width:100%;font-size:12px;padding:8px 12px;">+ QUICK ADD</button>`
              : `<button class="button button--secondary product-card__atc" disabled>SOLD OUT</button>`}
          </div>

        </div>

        <div class="product-card__info">
          <div class="v-stack gap-0.5 w-full">
            <div style="display:flex;align-items:flex-start;justify-content:space-between;gap:8px;">
              <span class="product-card__title" style="margin:0;flex:1;word-wrap:break-word;overflow-wrap:break-word;line-height:1.3;max-width:85%;">
                <a href="${esc(url)}" class="bold" style="display:block;word-break:break-word;">${esc(title)}</a>
              </span>
            </div>
            <div style="color:#000;font-size:15px;margin-top:4px;">
              MRP ₹ ${(comparePrice / 100).toLocaleString('en-IN')}
            </div>
          </div>
        </div>

      </product-card>
    </div>`;
  }

  // ── Empty state UI ──────────────────────────────────────
  function showEmptyState() {
    const container = document.getElementById('wl-product-grid');
    const heading   = document.getElementById('wl-page-heading');
    if (heading) heading.textContent = 'My Wishlist (0 Items)';
    if (container) container.innerHTML = `
      <div style="grid-column:1/-1;display:flex;flex-direction:column;align-items:center;justify-content:center;padding:60px 20px;text-align:center;">
        <h2 style="font-size:18px;font-weight:700;margin:0 0 10px;">Your Wishlist is empty.</h2>
        <p style="color:#888;font-size:14px;margin:0 0 24px;">Browse products and save the ones<br>you love.</p>
        <a href="/collections/all" style="display:inline-block;background:#5C301E;color:#fff;font-weight:700;font-size:13px;letter-spacing:0.1em;padding:14px 36px;text-decoration:none;transition:background 0.2s;">BROWSE PRODUCTS</a>
      </div>`;
  }

  const WL_PAGE_SIZE = 10;
  let wlCurrentPage = 1;

  function getWlPaginationEl(container) {
    let el = document.getElementById('wl-pagination');
    if (el) return el;
    el = document.createElement('div');
    el.id = 'wl-pagination';
    el.className = 'wl-pagination';
    container.insertAdjacentElement('afterend', el);
    return el;
  }

  function renderWlPagination(container, totalItems, onPageChange) {
    const pagEl = getWlPaginationEl(container);
    const totalPages = Math.ceil(totalItems / WL_PAGE_SIZE);
    if (totalPages <= 1) { pagEl.innerHTML = ''; pagEl.hidden = true; return; }
    pagEl.hidden = false;

    let html = `<button type="button" class="wl-page-btn wl-page-btn--nav" data-wl-page="prev" ${wlCurrentPage === 1 ? 'disabled' : ''}>‹</button>`;
    for (let i = 1; i <= totalPages; i++) {
      html += `<button type="button" class="wl-page-btn ${i === wlCurrentPage ? 'is-active' : ''}" data-wl-page="${i}">${i}</button>`;
    }
    html += `<button type="button" class="wl-page-btn wl-page-btn--nav" data-wl-page="next" ${wlCurrentPage === totalPages ? 'disabled' : ''}>›</button>`;
    pagEl.innerHTML = html;

    pagEl.querySelectorAll('[data-wl-page]').forEach(btn => {
      btn.addEventListener('click', () => {
        const val = btn.getAttribute('data-wl-page');
        if (val === 'prev') wlCurrentPage = Math.max(1, wlCurrentPage - 1);
        else if (val === 'next') wlCurrentPage = Math.min(totalPages, wlCurrentPage + 1);
        else wlCurrentPage = parseInt(val, 10);
        onPageChange(wlCurrentPage);
        container.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      });
    });
  }

  async function renderWishlistPage() {
    const container = document.getElementById('wl-product-grid');
    const heading   = document.getElementById('wl-page-heading');
    if (!container) return;

    const items = getLocalItems();
    const data  = getLocalData();
    const ids   = Object.keys(items);

    if (!ids.length) {
      wlCurrentPage = 1;
      const pagEl = document.getElementById('wl-pagination');
      if (pagEl) { pagEl.innerHTML = ''; pagEl.hidden = true; }
      showEmptyState();
      return;
    }

    // Show skeleton loaders
    container.innerHTML = ids.map(() => `
      <div class="product-card-wrapper wl-product-card-wrapper" style="opacity:0.4;">
        <product-card class="product-card product-card--blends">
          <div class="product-card__figure">
            <div style="aspect-ratio:1/1;background:#eee;display:block;"></div>
          </div>
          <div class="product-card__info">
            <div style="height:14px;background:#eee;border-radius:4px;margin-bottom:8px;"></div>
            <div style="height:14px;background:#eee;border-radius:4px;width:60%;"></div>
          </div>
        </product-card>
      </div>`).join('');

    const cards = await Promise.all(ids.map(async id => {
      const localData = data[id];
      if (!localData || !localData.product_handle) return '';
      const shopifyProduct = await fetchProduct(localData.product_handle);
      return buildCard(localData, shopifyProduct);
    }));

    // Filter out empty strings (invalid/missing products) and clean up stale localStorage entries
    const validCards = cards.filter(c => c !== '');
    ids.forEach((id, i) => {
      if (cards[i] === '') {
        setLocalItem(id, false);
        removeLocalData(id);
      }
    });

    // If all cards were invalid, show empty state
    if (!validCards.length) {
      showEmptyState();
      return;
    }

    if (heading) heading.textContent = `My Wishlist - ${validCards.length} Item${validCards.length === 1 ? '' : 's'}`;

    const totalPages = Math.ceil(validCards.length / WL_PAGE_SIZE) || 1;
    wlCurrentPage = Math.min(wlCurrentPage, totalPages);

    function renderPage(page) {
      wlCurrentPage = page;
      const start = (page - 1) * WL_PAGE_SIZE;
      container.innerHTML = validCards.slice(start, start + WL_PAGE_SIZE).join('');
      updateAllButtons();
      renderWlPagination(container, validCards.length, renderPage);
    }
    renderPage(wlCurrentPage);
  }

  async function removeFromWishlistAndRefresh(productId) {
    const phone = resolvedPhone();
    if (phone) {
      try { await apiRemove(phone, productId); } catch (e) { /* silent */ }
    }
    setLocalItem(productId, false);
    removeLocalData(productId);
    updateAllButtons();
    showToast('Removed from wishlist', 'success');
    renderWishlistPage();
  }

  // ── Add to Cart via AJAX (for wishlist page) ────────────
  window.submitWishlistCart = function(event, formId) {
    event.preventDefault();
    const form = document.getElementById(formId);
    if (!form) return;

    const formData = new FormData(form);
    const button = event.target;
    const originalText = button.textContent;
    
    button.disabled = true;
    button.textContent = 'Adding...';

    fetch(form.action, {
      method: 'POST',
      body: formData,
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
    .then(response => response.json())
    .then(data => {
      // Fetch the updated cart to get accurate count
      return fetch('/cart.json')
        .then(res => res.json())
        .then(cart => {
          // Dispatch cart:change event with correct data structure
          const event = new CustomEvent('cart:change', {
            detail: {
              cart: cart,
              item_count: cart.item_count,
              total_price: cart.total_price
            }
          });
          document.documentElement.dispatchEvent(event);
          
          button.textContent = 'Added!';
          setTimeout(() => {
            button.disabled = false;
            button.textContent = originalText;
          }, 800);
        });
    })
    .catch(error => {
      console.error('Add to cart error:', error);
      button.disabled = false;
      button.textContent = originalText;
    });
  };

  function scheduleServerSync() {
    const run = () => syncFromServer();
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(run, { timeout: 2500 });
    } else {
      setTimeout(run, 1200);
    }
  }

  function boot() {
    updateAllButtons();
    updateCounter();
    initButtons();
    initModal();
    // Event-driven counter/button refresh. Replaces the old permanent 1s setInterval(updateCounter).
    // setLocalItem() and syncFromServer() already refresh the counter themselves; these cover the other cases.
    const refreshState = () => { updateCounter(); updateAllButtons(); };
    window.addEventListener('storage', (e) => {                      // wishlist changed in another tab
      if (!e.key || e.key === LS_ITEMS_KEY) refreshState();
    });
    window.addEventListener('pageshow', (e) => { if (e.persisted) refreshState(); });   // back/forward cache restore
    document.addEventListener('shopify:section:load', refreshState);                    // theme editor section reload
    document.addEventListener('nq:content-added', refreshState);                        // NQ: lazily built rows (collection sub-section)
    document.addEventListener('visibilitychange', () => { if (!document.hidden) updateCounter(); });
    // Local state is usable immediately. Synchronize in idle time so the API
    // request does not compete with first render.
    scheduleServerSync();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  window.WishlistPlugin = {
    updateAllButtons,
    updateCounter,
    openModal,
    getLocalItems,
    getLocalData,
    isWishlisted,
    apiRemove,
    setLocalItem,
    removeLocalData,
    getPhone: resolvedPhone,
    getName:  resolvedName,
    removeFromWishlistAndRefresh,
  };

  window.WishlistPage = {
    refresh: renderWishlistPage,
    buildCard,
    fetchProduct,
  };

})();